/**
 * <s-sticky-buy> — mobile-only bar pinned to the bottom of the product page with
 * Add to cart and Buy now.
 *
 * It lives inside the product form (and the 'product-form' partial), so its buttons submit
 * the same variant and quantity as the main buttons and it re-renders on variant change.
 * The bar stays hidden (and inert) while the main product form is on screen, and slides in
 * once the shopper scrolls past it. While shown on mobile, the page gets bottom padding equal
 * to the bar's height so the footer is never covered.
 *
 * Expected DOM:
 *   <form …><s-sticky-buy data-watch="[data-product-form]">…buttons…</s-sticky-buy></form>
 */
class SStickyBuy extends HTMLElement {
  connectedCallback() {
    this.mobile = window.matchMedia("(max-width: 767px)");
    this.target = this.closest(this.dataset.watch || "[data-product-form]");
    this.onMediaChange = () => this.#apply();
    this.mobile.addEventListener("change", this.onMediaChange);

    this.targetVisible = true;
    if (this.target) {
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) this.targetVisible = entry.isIntersecting;
        this.#apply();
      });
      this.observer.observe(this.target);
    }
    this.#apply();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mobile?.removeEventListener("change", this.onMediaChange);
    document.body.style.removeProperty("padding-bottom");
  }

  #apply() {
    const show = this.mobile.matches && !this.targetVisible;
    this.toggleAttribute("data-visible", show);
    this.inert = !show;
    this.setAttribute("aria-hidden", show ? "false" : "true");

    // Reserve space only while the bar is shown; padding while hidden left a blank strip below the footer.
    if (show) {
      document.body.style.paddingBottom = `${this.offsetHeight}px`;
    } else {
      document.body.style.removeProperty("padding-bottom");
    }
  }
}

if (!customElements.get("s-sticky-buy")) customElements.define("s-sticky-buy", SStickyBuy);
